import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiCamera, FiUpload, FiX, FiAlertTriangle, FiCheckCircle, FiAlertCircle, FiFileText, FiUser, FiBriefcase, FiBook, FiGlobe, FiClock, FiHash, FiMapPin } from 'react-icons/fi';

const API_URL = `${process.env.REACT_APP_API_URL}/api/lawyers/profile.php`;

const inputClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed';
const selectClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white disabled:bg-gray-100 disabled:cursor-not-allowed';
const textareaClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none disabled:bg-gray-100 disabled:cursor-not-allowed';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

function LawyerProfileSetup({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [existingProfile, setExistingProfile] = useState(null);

  const [formData, setFormData] = useState({
    specialization: '',
    experience_years: '',
    hourly_rate: '',
    license_number: '',
    bar_association: '',
    bio: '',
    education: '',
    languages: '',
    availability: 'full-time'
  });

  const specializations = [
    'Criminal Law',
    'Family Law',
    'Corporate Law',
    'Real Estate',
    'Immigration Law',
    'Intellectual Property',
    'Tax Law',
    'Personal Injury',
    'Civil Litigation',
    'Contract Law',
    'Bankruptcy Law',
    'Employment Law',
    'Environmental Law',
    'Constitutional Law',
    'International Law',
    'Other'
  ];

  // Fetch existing profile if lawyer already has one
  useEffect(() => {
    if (user?.id) {
      fetchExistingProfile();
    }
  }, [user?.id]);

  const fetchExistingProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}?user_id=${user.id}`);
      if (response.data.success && response.data.data) {
        const profile = response.data.data;
        setExistingProfile(profile);
        setFormData({
          specialization: profile.specialization || '',
          experience_years: profile.experience_years?.toString() || '',
          hourly_rate: profile.hourly_rate?.toString() || '',
          license_number: profile.license_number || '',
          bar_association: profile.bar_association || '',
          bio: profile.bio || '',
          education: profile.education || '',
          languages: profile.languages || '',
          availability: profile.availability || 'full-time'
        });
        if (profile.profile_image) {
          setProfileImagePreview(profile.profile_image);
        }
      }
    } catch (err) {
      console.log('No existing profile found');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Profile image must be less than 5MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        setError('Only JPEG and PNG images are allowed for profile');
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePortfolioChange = (e) => {
    const files = Array.from(e.target.files);
    if (portfolioFiles.length + files.length > 5) {
      setError('Maximum 5 portfolio files allowed');
      return;
    }
    
    for (let file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name} is too large (max 10MB)`);
        return;
      }
    }
    
    setPortfolioFiles(prev => [...prev, ...files]);
  };

  const removePortfolioFile = (index) => {
    setPortfolioFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.specialization) {
      setError('Please select a specialization');
      return;
    }
    if (!formData.experience_years || formData.experience_years < 0) {
      setError('Please enter valid years of experience');
      return;
    }
    if (!formData.hourly_rate || formData.hourly_rate < 0) {
      setError('Please enter valid hourly rate');
      return;
    }
    if (!formData.license_number) {
      setError('License number is required');
      return;
    }
    if (!formData.bar_association) {
      setError('Bar association is required');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('user_id', user.id);
      formDataToSend.append('specialization', formData.specialization);
      formDataToSend.append('experience_years', formData.experience_years);
      formDataToSend.append('hourly_rate', formData.hourly_rate);
      formDataToSend.append('license_number', formData.license_number);
      formDataToSend.append('bar_association', formData.bar_association);
      formDataToSend.append('bio', formData.bio);
      formDataToSend.append('education', formData.education);
      formDataToSend.append('languages', formData.languages);
      formDataToSend.append('availability', formData.availability);

      if (profileImage) {
        formDataToSend.append('profile_image', profileImage);
      }

      portfolioFiles.forEach((file, index) => {
        formDataToSend.append(`portfolio_files`, file);
      });

      let response;
      if (existingProfile) {
        response = await axios.put(API_URL, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(API_URL, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.data.success) {
        setSuccess(existingProfile ? 'Profile updated successfully! Pending admin approval.' : 'Profile created successfully! Your profile is pending admin approval.');
        setExistingProfile(response.data.data);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to save profile');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (user?.user_type !== 'lawyer') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-card p-8 max-w-md w-full text-center">
          <FiAlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <p className="text-gray-600 font-medium">You must be registered as a lawyer to access this page.</p>
        </div>
      </div>
    );
  }

  const statusColors = {
    approved: 'bg-green-50 text-green-700',
    pending: 'bg-amber-50 text-amber-700',
    rejected: 'bg-red-50 text-red-700',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary via-primary-light to-[#2a4a6f] text-white py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-2xl sm:text-3xl font-bold flex items-center justify-center gap-3">
            <FiBriefcase className="w-7 h-7" /> Complete Your Lawyer Profile
          </motion.h1>
          <p className="mt-2 text-white/70 text-sm">Add your professional details and get approved to appear on our lawyer directory</p>
          {existingProfile && (
            <div className="mt-4">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${statusColors[existingProfile.approval_status] || 'bg-gray-100 text-gray-600'}`}>
                Status: {existingProfile.approval_status.toUpperCase()}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4">
        {/* Alerts */}
        {error && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 text-red-700 rounded-xl text-sm mb-6">
            <FiAlertCircle className="w-5 h-5 flex-shrink-0" /> {error}
          </motion.div>
        )}
        {success && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 p-4 bg-green-50 border border-green-100 text-green-700 rounded-xl text-sm mb-6">
            <FiCheckCircle className="w-5 h-5 flex-shrink-0" /> {success}
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl shadow-card p-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4"><FiCamera className="w-5 h-5 text-primary" /> Profile Picture</h2>
            <div className="flex flex-col items-center gap-4">
              <label htmlFor="profile-image-input" className="cursor-pointer group">
                {profileImagePreview ? (
                  <div className="w-28 h-28 rounded-full overflow-hidden ring-4 ring-primary/10 group-hover:ring-primary/30 transition-all">
                    <img src={profileImagePreview} alt="Profile" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-28 h-28 rounded-full bg-gray-100 flex flex-col items-center justify-center gap-1 group-hover:bg-primary/5 transition-colors ring-4 ring-gray-100 group-hover:ring-primary/10">
                    <FiCamera className="w-6 h-6 text-gray-400 group-hover:text-primary transition-colors" />
                    <span className="text-xs text-gray-400 group-hover:text-primary transition-colors">Upload</span>
                  </div>
                )}
              </label>
              <input
                id="profile-image-input"
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleProfileImageChange}
                className="hidden"
                disabled={loading}
              />
              <p className="text-xs text-gray-400">Max 5MB. JPEG or PNG format.</p>
            </div>
          </motion.div>

          {/* Professional Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl shadow-card p-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-5"><FiBriefcase className="w-5 h-5 text-primary" /> Professional Information</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="specialization" className={labelClass}>Specialization *</label>
                <select
                  id="specialization"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleInputChange}
                  className={selectClass}
                  required
                  disabled={loading}
                >
                  <option value="">Select your specialization</option>
                  {specializations.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="experience_years" className={labelClass}>Years of Experience *</label>
                  <input
                    type="number"
                    id="experience_years"
                    name="experience_years"
                    value={formData.experience_years}
                    onChange={handleInputChange}
                    className={inputClass}
                    min="0"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="hourly_rate" className={labelClass}>Hourly Rate (₹) *</label>
                  <input
                    type="number"
                    id="hourly_rate"
                    name="hourly_rate"
                    value={formData.hourly_rate}
                    onChange={handleInputChange}
                    className={inputClass}
                    min="0"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="license_number" className={labelClass}>License Number *</label>
                  <input
                    type="text"
                    id="license_number"
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="e.g., BAR/2020/12345"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="bar_association" className={labelClass}>Bar Association *</label>
                  <input
                    type="text"
                    id="bar_association"
                    name="bar_association"
                    value={formData.bar_association}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="e.g., Delhi Bar Association"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="availability" className={labelClass}>Availability</label>
                <select
                  id="availability"
                  name="availability"
                  value={formData.availability}
                  onChange={handleInputChange}
                  className={selectClass}
                  disabled={loading}
                >
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="limited">Limited Availability</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Additional Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-2xl shadow-card p-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-5"><FiBook className="w-5 h-5 text-primary" /> Additional Information</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="bio" className={labelClass}>Professional Bio</label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  className={textareaClass}
                  placeholder="Write a brief professional bio about yourself..."
                  rows="4"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="education" className={labelClass}>Education & Qualifications</label>
                <textarea
                  id="education"
                  name="education"
                  value={formData.education}
                  onChange={handleInputChange}
                  className={textareaClass}
                  placeholder="e.g., BA in Law from Delhi University, LLM from National Law University..."
                  rows="3"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="languages" className={labelClass}>Languages Spoken</label>
                <input
                  type="text"
                  id="languages"
                  name="languages"
                  value={formData.languages}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="e.g., English, Hindi, Marathi"
                  disabled={loading}
                />
              </div>
            </div>
          </motion.div>

          {/* Portfolio Upload */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl shadow-card p-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2"><FiFileText className="w-5 h-5 text-primary" /> Portfolio Documents</h2>
            <p className="text-sm text-gray-500 mb-5">Upload case summaries, certifications, or other professional documents (max 5 files, 10MB each)</p>

            <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-200 rounded-xl hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer">
              <FiUpload className="w-6 h-6 text-gray-400" />
              <span className="text-sm text-gray-500">Click to upload files</span>
              <span className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG</span>
              <input
                type="file"
                multiple
                onChange={handlePortfolioChange}
                className="hidden"
                disabled={loading}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </label>

            {portfolioFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Files to Upload ({portfolioFiles.length})</h3>
                {portfolioFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <FiFileText className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">{file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">({(file.size / 1024 / 1024).toFixed(2)}MB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePortfolioFile(index)}
                      className="w-7 h-7 rounded-full hover:bg-red-50 flex items-center justify-center transition-colors flex-shrink-0"
                      disabled={loading}
                    >
                      <FiX className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Submit Buttons */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="flex flex-col sm:flex-row items-center gap-3">
            <button
              type="submit"
              className="w-full sm:w-auto px-8 py-3 text-sm font-semibold text-white bg-primary rounded-xl hover:bg-primary-light shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : (existingProfile ? 'Update Profile' : 'Submit for Approval')}
            </button>
            <button
              type="button"
              className="w-full sm:w-auto px-8 py-3 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Cancel
            </button>
          </motion.div>

          {/* Note */}
          <div className="flex items-start gap-2.5 p-4 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-amber-700">
            <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>Your profile will be reviewed by our admin team. You'll receive an email once it's approved.</p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LawyerProfileSetup;
